/**
 * Accredited investor qualification categories — captured during institution wizard.
 */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
} from '@mui/material';
import { TOUCH, FONT_SIZE, SPACING } from '@/core/platform/platformStandards';
import {
  ACCREDITED_QUALIFICATION_CATEGORIES,
} from '@/features/auth/registration/constants/accreditedQualificationCategories';

const QualificationCategoriesStep = ({
  values,
  errors,
  setFieldValue,
  stepFlagged,
}) => {
  const selected = values.qualificationCategories || [];
  const showError = stepFlagged && Boolean(errors.qualificationCategories);

  const handleToggle = (categoryId) => {
    const next = selected.includes(categoryId)
      ? selected.filter((id) => id !== categoryId)
      : [...selected, categoryId];
    setFieldValue('qualificationCategories', next, false);
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
        Accredited investor qualifications
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select all SEC Rule 501 categories that apply to your institution. These selections
        appear on your accredited investor declaration when you sign.
      </Typography>

      {showError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.qualificationCategories}
        </Alert>
      )}

      <Box
        sx={{
          p: 2,
          bgcolor: 'background.default',
          borderRadius: 1,
          border: 1,
          borderColor: showError ? 'error.main' : 'divider',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: SPACING.md }}>
          Category of qualification (check all that apply)
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {ACCREDITED_QUALIFICATION_CATEGORIES.map((category) => (
            <FormControlLabel
              key={category.id}
              control={
                <Checkbox
                  checked={selected.includes(category.id)}
                  onChange={() => handleToggle(category.id)}
                  color="primary"
                  sx={{ minWidth: TOUCH.minSize, minHeight: TOUCH.minSize }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: FONT_SIZE.sm }}>
                  {category.label}
                </Typography>
              }
              sx={{ alignItems: 'flex-start', ml: 0 }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

QualificationCategoriesStep.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  stepFlagged: PropTypes.bool,
};

QualificationCategoriesStep.defaultProps = {
  stepFlagged: false,
};

export default QualificationCategoriesStep;
